import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { ReactComponent as StethoscopeIcon } from "./icons/stethoscope.svg";
import { ReactComponent as CalendarBlankIcon } from "./icons/calendar-blank.svg";
import { ReactComponent as ClockSvgIcon } from "./icons/clock.svg";
import { ReactComponent as UserBoldIcon } from "./icons/user-bold.svg";
import { useSystemLogo } from "../../context/systemConfigContext";

export default function SeleccionarTurnoPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logo } = useSystemLogo();

    const patient = location.state?.patient ?? null;

    const doctor =
        location.state?.doctor ??
        ({ name: "Dr. Carlos Mendoza", specialty: "Cardiología" });

    const patientName = location.state?.patientName ?? null;

    const months = useMemo(() => {
        const monthNames = [
            "Enero",
            "Febrero",
            "Marzo",
            "Abril",
            "Mayo",
            "Junio",
            "Julio",
            "Agosto",
            "Septiembre",
            "Octubre",
            "Noviembre",
            "Diciembre",
        ];
        const currentMonthIndex = new Date().getMonth();

        return monthNames.slice(currentMonthIndex);
    }, []);

    const [selectedMonth, setSelectedMonth] = useState(months[0]);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [datesLoading, setDatesLoading] = useState(false);
    const [timesLoading, setTimesLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmLoading, setConfirmLoading] = useState(false);

    const doctorId = doctor?.raw?.id ?? doctor?.id;

    const monthNameToNumber = (name) => {
        const monthNames = [
            "Enero",
            "Febrero",
            "Marzo",
            "Abril",
            "Mayo",
            "Junio",
            "Julio",
            "Agosto",
            "Septiembre",
            "Octubre",
            "Noviembre",
            "Diciembre",
        ];
        const idx = monthNames.findIndex((m) => m === name);
        return idx >= 0 ? idx + 1 : null;
    };

    const getTodayIsoLocal = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const timeToMinutes = (t) => {
        const [hh, min] = (t ?? "").split(":");
        const h = Number(hh);
        const m = Number(min);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
    };

    const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

    const dateToChip = (isoDate) => {
        const dt = new Date(`${isoDate}T00:00:00`);
        const weekday = capitalize(
            dt.toLocaleDateString("es-ES", { weekday: "long" })
        );
        const day = dt.getDate();
        return { label: `${weekday} ${day}`, value: isoDate };
    };

    const selectedDateChip = selectedDate ? dateToChip(selectedDate) : null;

    const dateLabel = selectedDateChip
        ? `${selectedDateChip.label} de ${selectedMonth.toLowerCase()}`
        : "";
    const timeLabel = selectedTime ? `${selectedTime} hs` : "";

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            const month = monthNameToNumber(selectedMonth);
            const year = new Date().getFullYear();
            if (!doctorId || !month) return;
            try {
                setErrorMessage("");
                setDatesLoading(true);
                setAvailableDates([]);
                setAvailableTimes([]);
                setSelectedDate("");
                setSelectedTime("");
                const data = await api.availability.getAvailableDates(
                    doctorId,
                    month,
                    year
                );
                if (!mounted) return;
                const todayIso = getTodayIsoLocal();
                const dates = (Array.isArray(data) ? data : []).filter(
                    (iso) => typeof iso === "string" && iso >= todayIso
                );
                setAvailableDates(dates);
                const first = dates[0] ?? "";
                setSelectedDate(first);
            } catch (e) {
                if (!mounted) return;
                setAvailableDates([]);
                setSelectedDate("");
                setAvailableTimes([]);
                setSelectedTime("");
                setErrorMessage(
                    typeof e?.message === "string"
                        ? e.message
                        : "No pudimos cargar la disponibilidad. Intentá nuevamente."
                );
            } finally {
                if (mounted) setDatesLoading(false);
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, [doctorId, selectedMonth]);

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            if (!doctorId || !selectedDate) return;
            try {
                setErrorMessage("");
                setTimesLoading(true);
                setAvailableTimes([]);
                setSelectedTime("");
                const data = await api.availability.getAvailableTimes(
                    doctorId,
                    selectedDate
                );
                if (!mounted) return;
                const rawTimes = Array.isArray(data) ? data : [];
                const todayIso = getTodayIsoLocal();
                const now = new Date();
                const nowMinutes = now.getHours() * 60 + now.getMinutes();

                const times = rawTimes.filter((t) => {
                    if (typeof t !== "string") return false;
                    if (selectedDate !== todayIso) return true;
                    const mins = timeToMinutes(t);
                    if (mins === null) return false;
                    return mins > nowMinutes;
                });
                setAvailableTimes(times);
                setSelectedTime(times[0] ?? "");
            } catch (e) {
                if (!mounted) return;
                setAvailableTimes([]);
                setSelectedTime("");
                setErrorMessage(
                    typeof e?.message === "string"
                        ? e.message
                        : "No pudimos cargar los horarios. Intentá nuevamente."
                );
            } finally {
                if (mounted) setTimesLoading(false);
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, [doctorId, selectedDate]);

    const chipClass = (active) =>
        active
            ? "bg-[#465FFF] text-white border-[#465FFF]"
            : "bg-white text-[#111827] border-[#ECECFF]";

    const onConfirm = async () => {
        const doctorId = doctor?.raw?.id ?? doctor?.id;
        const patientId = patient?.id;

        if (!doctorId || !patientId || !selectedDate || !selectedTime) return;

        const payload = {
            patient: patientId,
            doctor: doctorId,
            unscheduled: false,
            date: selectedDate,
            time: selectedTime,
            appointment_type: 1,
            notes: "",
            contact_lens: false,
            dilatate: false,
        };

        try {
            setErrorMessage("");
            setConfirmLoading(true);
            let appointment = null;
            try {
                appointment = await api.appointments.create(payload);
            } catch (e) {
                if (e?.code === "unauthorized") {
                    appointment = await api.publicAppointments.create(payload);
                } else {
                    throw e;
                }
            }

            navigate("/reserve/confirmed", {
                state: {
                    appointment,
                    doctor,
                    patient,
                    patientName,
                    dateLabel,
                    timeLabel,
                },
            });
        } catch (e) {
            setErrorMessage(
                typeof e?.message === "string"
                    ? e.message
                    : "No pudimos agendar la cita. Intentá nuevamente."
            );
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F1F1FE] px-4 py-6 font-poppins md:flex md:items-center md:justify-center">
            <div className="w-full max-w-sm mx-auto md:max-w-[640px]">
                <div className="hidden md:block">
                    <div className="relative flex items-center justify-center">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="absolute left-0 flex items-center gap-2 text-[#000000] font-medium"
                        >
                            <div className="h-7 w-7 rounded-full bg-[#465FFF] flex items-center justify-center">
                                <ChevronLeftIcon className="h-4 w-4 text-white" />
                            </div>
                            <span>Atrás</span>
                        </button>
                        <img className="h-auto w-40" src={logo} alt="Logo" />
                    </div>
                </div>

                <div className="md:hidden">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#000000] font-medium"
                    >
                        <div className="h-7 w-7 rounded-full bg-[#465FFF] flex items-center justify-center">
                            <ChevronLeftIcon className="h-4 w-4 text-white" />
                        </div>
                        <span>Atrás</span>
                    </button>
                </div>

                <div className="mt-6 bg-white rounded-2xl shadow-sm px-5 py-6">
                    <div className="bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-3 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#EFEFFF] flex items-center justify-center">
                            <UserBoldIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="text-xl font-medium tracking-[0.5px] text-[#465FFF]">
                                {doctor.name}
                            </div>
                            <div className="text-sm text-[#495974]">
                                {doctor.specialty}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-4">
                        <div className="flex items-center gap-2">
                            <CalendarBlankIcon className="h-6 w-6" />
                            <div className="text-lg font-medium text-[#000000]">
                                1. Seleccionar Mes
                            </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                            {months.map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() => setSelectedMonth(m)}
                                    className={`px-3 py-1.5 text-base font-medium rounded-lg border ${chipClass(
                                        selectedMonth === m
                                    )}`}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-3 bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-4">
                        <div className="flex items-center gap-2">
                            <CalendarBlankIcon className="h-6 w-6" />
                            <div className="text-lg font-medium text-[#000000]">
                                2. Fechas disponibles
                            </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                            {datesLoading ? (
                                <div className="flex items-center justify-center py-4 w-full">
                                    <div className="w-6 h-6 border-b-2 border-[#465FFF] rounded-full animate-spin" />
                                </div>
                            ) : null}

                            {!datesLoading && availableDates.length === 0 ? (
                                <div className="text-center text-sm text-gray-500 py-4 w-full">
                                    No hay fechas disponibles.
                                </div>
                            ) : null}

                            {!datesLoading
                                ? availableDates.map((iso) => {
                                      const chip = dateToChip(iso);
                                      return (
                                          <button
                                              key={chip.value}
                                              type="button"
                                              onClick={() => setSelectedDate(chip.value)}
                                              className={`px-3 py-1.5 text-base font-medium rounded-lg border ${chipClass(
                                                  selectedDate === chip.value
                                              )}`}
                                          >
                                              {chip.label}
                                          </button>
                                      );
                                  })
                                : null}
                        </div>
                    </div>

                    <div className="mt-3 bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-4">
                        <div className="flex items-center gap-2">
                            <ClockSvgIcon className="h-6 w-6" />
                            <div className="text-lg font-medium text-[#000000]">
                                3. Horas disponibles
                            </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                            {timesLoading ? (
                                <div className="flex items-center justify-center py-4 w-full">
                                    <div className="w-6 h-6 border-b-2 border-[#465FFF] rounded-full animate-spin" />
                                </div>
                            ) : null}

                            {!timesLoading && selectedDate && availableTimes.length === 0 ? (
                                <div className="text-center text-sm text-gray-500 py-4 w-full">
                                    No hay horarios disponibles.
                                </div>
                            ) : null}

                            {!timesLoading
                                ? availableTimes.map((t) => (
                                      <button
                                          key={t}
                                          type="button"
                                          onClick={() => setSelectedTime(t)}
                                          className={`px-3 py-1.5 text-base font-medium rounded-lg border ${chipClass(
                                              selectedTime === t
                                          )}`}
                                      >
                                          {t}
                                      </button>
                                  ))
                                : null}
                        </div>
                    </div>

                    <div className="mt-5 bg-[#E3E2FF] border border-[#ECECFF] rounded-xl px-4 py-4">
                        <div className="text-center text-lg font-medium text-[#465FFF]">
                            Resumen de tu Cita
                        </div>

                        <div className="mt-4 flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-[#EFEFFF] flex items-center justify-center flex-shrink-0">
                                <StethoscopeIcon className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <div className="text-base font-medium text-[#000000]">
                                    {doctor.name}
                                </div>
                                <div className="text-sm font-medium text-[#465FFF]">
                                    {doctor.specialty}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 h-px w-full bg-[#ECECFF]" />

                        <div className="mt-4 flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-[#EFEFFF] flex items-center justify-center flex-shrink-0">
                                <CalendarBlankIcon className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <div className="text-base font-medium text-[#000000]">
                                    {dateLabel}
                                </div>
                                <div className="text-sm font-medium text-[#465FFF]">
                                    {timeLabel}
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={
                            !selectedDate ||
                            !selectedTime ||
                            datesLoading ||
                            timesLoading ||
                            confirmLoading ||
                            !patient?.id
                        }
                        className="mt-6 w-full rounded-lg bg-[#465FFF] text-white font-medium py-3 flex items-center justify-center"
                    >
                        {confirmLoading ? (
                            <div className="w-5 h-5 border-b-2 border-white rounded-full animate-spin" />
                        ) : (
                            "Confirmar turno"
                        )}
                    </button>

                    {errorMessage ? (
                        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
                            {errorMessage}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
